<template>
  <section
    v-if="selectedExpedientTopBarData && selectedExpedientTopBarData.length && !showSpinner"
    v-resize="checkTruncation"
    class="expedient-global-vision-header"
  >
    <!-- Primera fila con los dos primeros elementos -->
    <div class="first-row">
      <div v-for="(item, index) in firstRowData" :key="`first-${index}`" :class="[item.class, 'header-item']">
        <span>{{ item.label }}</span>
        <span v-if="isValidData(item.data)" class="data">{{ item.data }}</span>
      </div>
    </div>

    <!-- Segunda fila con el resto de elementos -->
    <div class="second-row">
      <div v-for="(item, index) in secondRowData" :key="`second-${index + 2}`" class="header-item">
        <span class="item-label">{{ item.label }}</span>
        <span v-if="isValidData(item.data)" class="data">
          <LexonTooltipComponent
            v-if="isTruncatedItems[index]"
            :message="item.data"
            :disabled="!isTruncatedItems[index]"
            position="top"
            isWidthAuto
          >
            <template #activator-element>
              <span :ref="(el) => setRef(el, index)" :class="['truncated', getSpecialClasses(index + 2)]">
                {{ item.data }}
              </span>
            </template>
          </LexonTooltipComponent>
          <span v-else :ref="(el) => setRef(el, index)" :class="['truncated', getSpecialClasses(index + 2)]">
            {{ item.data }}
          </span>
        </span>
      </div>
    </div>
  </section>

  <SpinnerLayerComponent v-else />
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { Entity } from '@/store/modules/entities/entitiesTypes'
import { ContextName } from '@/store/types/storeGlobalTypes'
import SpinnerLayerComponent from '@/components/Spinner/SpinnerLayerComponent.vue'
import useSelectedRegister from '@/composables/useSelectedRegister'
import useStage from '@/composables/useStage'
import useEntity from '@/composables/useEntity'
import useEndpoints from '@/composables/useEndpoints'
import LexonTooltipComponent from '@/components/forms/fields/LexonTooltip/LexonTooltipComponent.vue'
import useTruncation from '@/composables/useTruncation'

// CONSTS
const contexts = {
  topBarContext: ContextName.EXPEDIENT_GLOBAL_VISION_TOP_BAR,
  expedientContext: ContextName.EXPEDIENTS
}

// DATA
const showSpinner = ref(true)

// COMPOSABLES
const expedientRegister = useSelectedRegister({ entityName: contexts.expedientContext })
const topBarRegister = useSelectedRegister({ entityName: contexts.topBarContext })
const { selectedRegisterId: selectedExpedientId, loadSelectedRegisterWithPost } = expedientRegister
const { selectedRegisterData } = topBarRegister

const { expedientStage } = useStage()
const { getEntity } = useEntity({ entityName: contexts.expedientContext })
const { getEndpoints } = useEndpoints()

// COMPUTED
const selectedExpedientTopBarData = computed(() => {
  return selectedRegisterData.value?.globalVisionTopBar || []
})

const firstRowData = computed(() => selectedExpedientTopBarData.value.slice(0, 2))
const secondRowData = computed(() => selectedExpedientTopBarData.value.slice(2))
const expedientStageState = computed(() => expedientStage.value)

// METHODS
const fetchGlobalVisionTopBarInfo = async () => {
  showSpinner.value = true

  const entity: Entity = getEntity()
  const entityType = entity.type
  const endpoints = getEndpoints(entityType)
  const endpoint = endpoints.get

  await loadSelectedRegisterWithPost({
    endpoint,
    context: contexts.topBarContext,
    post: {
      idExpedient: selectedExpedientId.value,
      source: contexts.topBarContext,
      idStage: expedientStageState.value
    }
  })

  showSpinner.value = false
}

const getSpecialClasses = (index: number) => {
  const classMap: Record<number, string> = {
    2: 'procedure',
    3: 'stage',
    4: 'situation'
  }

  return classMap[index] || ''
}

const isValidData = (data: any): boolean => {
  return !!data && data !== ' '
}

const { isTruncatedItems, checkTruncation, setRef } = useTruncation({
  items: secondRowData.value
})

// WATCHERS
watch(expedientStageState, () => {
  fetchGlobalVisionTopBarInfo()
})

// INIT
fetchGlobalVisionTopBarInfo()
</script>

<style lang="scss" scoped>
$ellipsis-width: 140px;

.expedient-global-vision-header {
  display: flex;
  flex-direction: column;
  padding: $spacing-xs $spacing-md;
  align-items: flex-start;
  align-self: stretch;
  border-radius: $cornerRadius-md;
  background: $neutral-grey-100;
  gap: $spacing-sm;

  .first-row,
  .second-row {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: $spacing-sm;
  }

  .item-label {
    @include flex;
  }

  .header-item {
    @include milano-bold-16;
    color: $neutral-grey-800;
    display: flex;

    &:first-letter {
      text-transform: capitalize;
    }

    .data {
      @include milano-regular-14;
      display: block;
      margin-left: $spacing-xxs;
      overflow: hidden;
      line-height: 22.4px;
      height: 100%;
    }

    ::v-deep(.activator) {
      @include flex;
    }

    .truncated {
      @include milano-regular-14;
      margin-left: $spacing-xxs;
      max-width: 100%;
      width: 100%;
      display: inline-block;

      &.procedure {
        @include ellipsis;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        max-width: 100%;
      }

      &.stage {
        @include arial-regular-12;
        @include ellipsis;
        border-radius: $cornerRadius-sm;
        background: $tertiary-byzantineBlue-050;
        padding: $spacing-xxs $spacing-xs;
        max-width: $ellipsis-width;
      }

      &.situation {
        @include arial-regular-12;
        @include ellipsis;
        border-radius: $cornerRadius-sm;
        background: $tertiary-seaGreen-050;
        padding: $spacing-xxs $spacing-xs;
        max-width: $ellipsis-width;
      }
    }
  }
}

@media (min-width: 1024px) {
  .expedient-global-vision-header {
    gap: $spacing-sm;

    .first-row {
      flex-direction: row;
      justify-content: flex-end;
      gap: $spacing-xxl;

      .header-item {
        width: calc(50% - $spacing-sm);
      }
    }

    .second-row {
      flex-direction: row;
      justify-content: flex-end;
      gap: $spacing-xxl;

      .header-item {
        overflow: hidden;

        .data {
          @include flex;
        }

        .procedure {
          max-width: 300px;
        }
      }
    }
  }
}

@media (min-width: 1536px) {
  .expedient-global-vision-header {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: $spacing-xxl;

    .first-row,
    .second-row {
      flex-direction: row;
      width: auto;
      height: 24px;
      gap: $spacing-xxl;
    }

    .second-row {
      .header-item {
        .procedure {
          max-width: 400px;
        }
      }
    }
  }
}
</style>
